import React, { useCallback, useEffect } from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import { useSuspenselessRefetch } from '@jira/issue__use-suspenseless-refetch/src/index.tsx';
import BoardIcon from '@atlaskit/icon/core/board';
import { useSubscribeToFavorite } from '@atlassian/jira-favourite-change-provider/src/common/utils/favorite-pub-sub/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useProjectsAnalyticsL2 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l2/index.tsx';
import {
	ENTITY_ID,
	MENU_ID_BOARDS_VIEW_ALL,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useSidebarNav4Controller } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-controller-context/index.tsx';
import LinkMenuItem from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { getTestId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { ViewAllEntityMenuItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/view-all-entity-menu-item/index.tsx';
import {
	BoardActions,
	type BoardWithDataId,
} from '@atlassian/jira-navigation-board-action-menu/src/ui/BoardActions.tsx';
import type { JiraLinkConfig } from '@atlassian/jira-router-components/src/ui/router-link-config/index.tsx';
import { boardsDirectoryRoute } from '@atlassian/jira-router-routes-directories-routes/src/boardsDirectoryRoute.tsx';
import type { BoardType } from '@atlassian/jira-shared-types/src/rest/jira/board.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { usePathParam, useRouterActions } from '@atlassian/react-resource-router';
import { RedirectOnLoad } from '@atlassian/redirect-on-load';
import {
	NO_OF_ADDITIONAL_BOARDS_TO_SHOW,
	NO_OF_BOARDS_TO_FETCH,
} from '../../../../common/constants.tsx';
import RefetchQuery, {
	type JswBoardsRefetchQuery,
} from './__generated__/JswBoardsRefetchQuery.graphql';
import type { JswBoards$data, JswBoards$key } from './__generated__/JswBoards.graphql';
import messages from './messages.tsx';

type JswBoardsProps = {
	queryRef: JswBoards$key;
};

function buildBoardHref(
	projectKey: string,
	url?: string | null,
): { to: string; prefetch: 'hover' } {
	return {
		to: url ?? `/browse/${projectKey}`,
		prefetch: 'hover',
	};
}

type GqlBoardType = NonNullable<
	NonNullable<NonNullable<JswBoards$data['boards']>['edges']>[0]
>['node'];

const transformNodeToBoard = (node: GqlBoardType): BoardWithDataId | undefined => {
	const { __id, boardId, boardType, favouriteValue, name, canEdit } = node || {};
	const isPublicBoard = !!name;
	if (__id && boardType && boardId) {
		return isPublicBoard
			? {
					id: boardId.toString(),
					dataId: __id,
					admins: { users: [], groups: [] },
					canEdit: !!canEdit,
					name,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					type: boardType as BoardType,
					favourite: !!favouriteValue?.isFavourite,
				}
			: {
					id: boardId.toString(),
					dataId: __id,
					admins: { users: [], groups: [] },
					canEdit: !!canEdit,
					isPrivate: true,
				};
	}
};

export function JswBoards({ queryRef }: JswBoardsProps): React.JSX.Element {
	const { sendBoardNavigationItemInsideProjectsAnalyticsEvent, sendViewAllBoardsAnalyticsEvent } =
		useProjectsAnalyticsL2();

	const [project, refetch] = useRefetchableFragment<JswBoardsRefetchQuery, JswBoards$key>(
		graphql`
			fragment JswBoards on JiraProject
			@argumentDefinitions(noOfBoardsToFetch: { type: "Int!" })
			@refetchable(queryName: "JswBoardsRefetchQuery") {
				...BoardActions
				id
				isFavourite
				projectId @required(action: THROW)
				projectKey: key @required(action: THROW)
				projectType
				boards(first: $noOfBoardsToFetch) {
					edges {
						node {
							__id @required(action: THROW)
							id
							name
							boardId
							defaultNavigationItem {
								... on JiraNavigationItem {
									url
								}
							}
							favouriteValue {
								isFavourite
							}
							boardType
							canEdit
						}
					}
				}
				mostRecentlyViewedBoard {
					... on JiraBoard {
						boardId
					}
				}
				softwareBoardCount @required(action: THROW)
			}
		`,
		queryRef,
	);

	const [boardsSuspenselessRefetch] = useSuspenselessRefetch(RefetchQuery, refetch, {
		fetchPolicy: 'network-only',
	});

	const onSubscribeToFavorite = useCallback(() => {
		boardsSuspenselessRefetch({
			id: project.id,
			noOfBoardsToFetch: NO_OF_BOARDS_TO_FETCH,
		});
	}, [boardsSuspenselessRefetch, project.id]);

	useSubscribeToFavorite('JiraBoard', onSubscribeToFavorite);

	const { formatMessage } = useIntl();
	const { isSelectedPath } = useSidebarNav4();
	const { isControlledPath } = useSidebarNav4Controller();

	const viewAllBoardsInProjectHref = `${boardsDirectoryRoute.path}?projectLocation=${project.projectKey}`;

	const noOfFavouriteBoards =
		project.boards?.edges?.filter((edge) => edge?.node?.favouriteValue?.isFavourite).length ?? 0;

	const noOfBoardsToShow = Math.min(
		noOfFavouriteBoards + NO_OF_ADDITIONAL_BOARDS_TO_SHOW,
		NO_OF_BOARDS_TO_FETCH,
	);

	const boardsToShow = project?.boards?.edges?.slice(0, noOfBoardsToShow) ?? [];

	const showViewAllBoards = project.softwareBoardCount > noOfBoardsToShow;

	const mostRecentlyViewedBoardId = project.mostRecentlyViewedBoard?.boardId;

	const projId = ENTITY_ID.PROJECT(project.projectKey);
	const isProjectSelected = isSelectedPath(projId);
	const isAnyBoardSelected =
		boardsToShow?.some(
			(board) =>
				board &&
				board.node &&
				board.node.boardId &&
				isSelectedPath(ENTITY_ID.BOARD(board.node.boardId.toString())),
		) ?? false;
	const isProjectScoped = isProjectSelected && !isAnyBoardSelected;
	const projectKey = project.projectKey;

	const boardIdPath = usePathParam('boardId')[0];

	// the result from the JswBoards query can sometimes be out of sync - eg when creating a new board
	// .: if the project is selected, and we are on board-scoped routes (ie boardIdPath is not undefined)
	// we check if the mostRecentlyViewedBoard matches the current selected path
	// if mostRecentlyViewedBoard does not match, we refetch boards
	useEffect(() => {
		const isMostRecentlyViewedBoardOutofSync =
			isProjectSelected &&
			boardIdPath &&
			mostRecentlyViewedBoardId &&
			!isSelectedPath(ENTITY_ID.BOARD(mostRecentlyViewedBoardId.toString()));

		if (expVal('blu-6805-fix-board-sync', 'isEnabled', false)) {
			if (isMostRecentlyViewedBoardOutofSync) {
				boardsSuspenselessRefetch({
					id: project.id,
					noOfBoardsToFetch: NO_OF_BOARDS_TO_FETCH,
				});
			} else if (isProjectSelected && boardIdPath && mostRecentlyViewedBoardId) {
				// Even if most recently viewed board is in sync, we need to check if boards are in the correct order. If not, we need to refetch.
				const isMostRecentlyViewedBoardFavourite = project.boards?.edges?.find(
					(edge) => edge?.node?.boardId === mostRecentlyViewedBoardId,
				)?.node?.favouriteValue?.isFavourite;
				const expectedBoardIndex = isMostRecentlyViewedBoardFavourite ? 0 : noOfFavouriteBoards;
				const isBoardInCorrectPosition =
					mostRecentlyViewedBoardId === project.boards?.edges?.[expectedBoardIndex]?.node?.boardId;

				if (!isBoardInCorrectPosition) {
					boardsSuspenselessRefetch({
						id: project.id,
						noOfBoardsToFetch: NO_OF_BOARDS_TO_FETCH,
					});
				}
			}
		}
	}, [
		boardsSuspenselessRefetch,
		project.id,
		isProjectSelected,
		boardIdPath,
		mostRecentlyViewedBoardId,
		isSelectedPath,
		noOfFavouriteBoards,
		project.boards?.edges,
	]);

	const handleBoardClick = useCallback(
		(boardId: number) => () => {
			sendBoardNavigationItemInsideProjectsAnalyticsEvent({
				recent: !project.isFavourite,
				starred: project.isFavourite ?? false,
				projectId: project.projectId,
				boardId,
				projectType: project.projectType,
			});
		},
		[
			project.isFavourite,
			project.projectId,
			project.projectType,
			sendBoardNavigationItemInsideProjectsAnalyticsEvent,
		],
	);
	const handleAllBoardsClick = useCallback(
		() => () => {
			sendViewAllBoardsAnalyticsEvent({
				projectId: project.projectId,
				projectType: project.projectType,
			});
		},
		[project.projectId, project.projectType, sendViewAllBoardsAnalyticsEvent],
	);

	const { push } = useRouterActions();

	return (
		<>
			{boardsToShow?.map((edge, index) => {
				if (!edge?.node?.boardId) {
					return [];
				}

				const href = buildBoardHref(projectKey, edge.node.defaultNavigationItem?.url);

				return (
					<RedirectOnLoad
						key={edge.node.id}
						condition={index === 0 && !isAnyBoardSelected}
						href={href.to}
						redirect={push}
					>
						<LinkMenuItem<JiraLinkConfig>
							elemBefore={<BoardIcon label="" color="currentColor" />}
							href={href}
							testId={getTestId(ENTITY_ID.BOARD(edge.node.boardId.toString()))}
							actionsOnHover={
								<BoardActions
									board={transformNodeToBoard(edge.node)}
									projectFragment={project}
									placement="right"
									isParentProjectStarred={project.isFavourite}
									isBoardFavorite={edge.node.favouriteValue?.isFavourite ?? undefined}
									iconButtonSpacing="compact"
								/>
							}
							onClick={handleBoardClick(edge.node.boardId)}
							isSelected={
								isSelectedPath(ENTITY_ID.BOARD(edge.node.boardId.toString())) ||
								isControlledPath(ENTITY_ID.BOARD(edge.node.boardId.toString())) ||
								(isProjectScoped && edge.node.boardId === mostRecentlyViewedBoardId)
							}
						>
							{edge.node.name}
						</LinkMenuItem>
					</RedirectOnLoad>
				);
			})}
			{showViewAllBoards && (
				<ViewAllEntityMenuItem
					onClick={handleAllBoardsClick}
					href={viewAllBoardsInProjectHref}
					menuId={MENU_ID_BOARDS_VIEW_ALL}
				>
					{formatMessage(messages.viewAllBoardsNonFinal)}
				</ViewAllEntityMenuItem>
			)}
		</>
	);
}
